/* eslint-disable complexity */

import { ROUTE_COMMUNITY } from '~/contexts/community/router/routes'
import { ROUTE_DM } from '~/contexts/dm/router/routes'
import { ROUTE_NETWORK } from '~/contexts/network/router/routes'

import { router } from '~/router/router'

import type { TContentContext } from '~/features-shared/config-manager/interfaces/contexts'
import { EContentContext, EContext, IPageContext } from '~/features-shared/config-manager/interfaces/contexts'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { logger } from '~/features-shared/libs/utils/logger'

export function useContextManager() {
  /**
   * Current context value (Enum index number).
   * @readonly
   */
  const context = computed<EContext>(() => {
    // It can be used outside of component, so it must import global router instead of "useRoute"
    const route = router.currentRoute.value
    if (route.path !== '/' && route.meta && 'context' in route.meta)
      return router.currentRoute.value.meta.context as EContext

    logger.info('Current route doesn\'t have a context: ', route)

    return EContext.NULL
  })

  /**
   * Current context name (Enum index string).
   * @readonly
   */
  const contextKey = computed(() => (EContext[context.value || EContext.NULL]) as keyof typeof EContext)

  const contextClassName = computed(() => { // used for tracker so don't change
    const contextVal = context.value
    const route = router.currentRoute.value

    switch (contextVal) {
      case EContext.RITUALS:
        return 'rituals-context'
      case EContext.CALLS:
        return 'calls-context'
      case EContext.DM:
        return 'conversation-context'
      case EContext.AURA:
        return 'aura-context'
      case EContext.CONTACTS:
        return 'contacts-context'
      case EContext.ME:
        return 'me-context'
      case EContext.NETWORK:
        return 'network-context'
      case EContext.ADMIN:
        return 'admin-context'
      case EContext.ERROR:
        return 'error-context'
      default:
        if (route.path === '/')
          return 'left-column-context'
        if (route.path.startsWith('/home'))
          return 'whatsnew-context'
        if (route.path.startsWith('/network'))
          return 'network-context'
        return 'community-context' // To be extended as per need
    }
  })

  return {
    /** Context displayed in the router-view ("main column") */
    context,
    /** Context key (COMMUNITY, DM, etc...) displayed in the router-view ("main column") */
    contextKey,
    /** Class name related to the current context */
    contextClassName,
  }
}

export function useContentContextManager() {
  const { context } = useContextManager()
  // eslint-disable-next-line max-statements
  const contentContext = computed<TContentContext>(() => {
    const route = router.currentRoute.value

    if ([EContext.COMMUNITY, EContext.DM].includes(context.value)) {
      if ('thread_id' in route.params) {
        if (route.name === ROUTE_COMMUNITY.URI_FACETS_CONTENT || route.name === ROUTE_DM.URI_FACETS_CONTENT)
          return route.params.content_type || EContentContext.Post
        if (route.path.endsWith('/wiki'))
          return EContentContext.Wiki
        if (route.path.endsWith('/wedo'))
          return EContentContext.Wedo
        if (route.path.endsWith('/question'))
          return EContentContext.Question
        if (route.path.endsWith('/media'))
          return EContentContext.Media
        if (route.path.endsWith('/idea'))
          return EContentContext.Idea
        if (route.path.includes('/dt-idea-'))
          return EContentContext.DerivedIdea
        if (route.path.includes('/dt-post-'))
          return EContentContext.DerivedPost
        if (route.path.includes('/facet-'))
          return EContentContext.FacetExperience
        return EContentContext.Community
      }
    }

    if ([EContext.ME].includes(context.value)) {
      if (route.path.includes('/contributions'))
        return EContentContext.Contributions
      if (route.path.includes('/bookmarks'))
        return EContentContext.Bookmark
      if (route.path.includes('/drafts'))
        return EContentContext.GlobalDraft
      if (route.path.includes('/personal-space'))
        return EContentContext.PersonalSpace
      if (route.path.includes('/activity'))
        return EContentContext.Activity
    }
    if ([EContext.NETWORK].includes(context.value)) {
      if (route.name === ROUTE_NETWORK.USER_NETWORK)
        return EContentContext.Network
      if (route.name === ROUTE_NETWORK.USER_ACTIVITY)
        return EContentContext.Activity
      if (route.name === ROUTE_NETWORK.USER_PROFILE)
        return EContentContext.Profile
      else return EContentContext.GlobalNetwork
    }
    return EContentContext.All
  })

  return { context: contentContext }
}

/**
 * Page Context
 */
export function usePageContext() {
  const currentFilter = computed(() => {
    return Object.values(IContentModel)
      .find((e) => {
      // derivedIdea & derivedPost could not have it's model in the route but a particular path
        if (e === IContentModel.DerivedIdea)
          return router.currentRoute.value.path.includes(`/${e}`) || router.currentRoute.value.path.includes('/dt-idea')
        if (e === IContentModel.DerivedPost)
          return router.currentRoute.value.path.includes(`/${e}`) || router.currentRoute.value.path.includes('/dt-post')
        if (e === IContentModel.FacetExperience)
          return router.currentRoute.value.path.includes(`/${e}`) || router.currentRoute.value.path.includes('/facet-')
        return router.currentRoute.value.path.includes(`/${e}`)
      }) || null
  })

  // return page context, similar to route.path but route are filtered
  const currentPage = computed(() => {
    const route = router.currentRoute.value
    return Object.values(IPageContext)
      .find((e) => {
        if (currentFilter.value)
          return `${e}-${currentFilter.value}` === route.name
        return e === route.name
      }) || IPageContext
  })

  const currentPageMeta = computed(() => {
    const route = router.currentRoute.value
    return {
      name: currentPage.value,
      path: route.path,
      communityId: ('thread_id' in route.params && route.params.thread_id) || undefined,
      filteredBy: currentFilter,
      contentId: router.getQuery(route, 'content'),
      params: route.params,
      query: route.query,
      // we could add the page event title here to use it in tracker.ts
    }
  })

  return {
    currentPage,
    currentPageMeta,
  }
}
